<template>
  <v-row style="border: 1px solid gray; margin-bottom: 30px">
    <v-col cols="12">
      <h1>Migrate leads to a company</h1>
      <h3>
        Reassign a list of testators to a company's default advisor, with a
        preview/confirm step and a full undo.
      </h3>
      <p class="mb-1">
        Upload the lead list (CSV). The tool links each row to a testator by the
        <strong>Testator ID</strong> column and shows the testator's DB email so
        you can confirm the row matches before anything changes. Nothing is
        modified until you press <strong>Execute</strong>.
      </p>

      <v-row class="align-center mt-2">
        <v-col cols="12" md="3">
          <v-text-field
            v-model="companyId"
            label="Target company ID"
            type="number"
            outlined
            dense
            hide-details
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-file-input
            v-model="file"
            accept=".csv,text/csv"
            single-line
            outlined
            dense
            filled
            :loading="previewLoading"
            label="Upload lead list (CSV)"
            hide-details
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-btn
            rounded
            depressed
            block
            color="primary"
            :loading="previewLoading"
            :disabled="!file || !companyId"
            @click="preview()"
            ><span>Preview migration</span></v-btn
          >
        </v-col>
      </v-row>

      <v-row class="align-center">
        <v-col cols="12" md="6">
          <v-select
            v-model="selectedRecentId"
            :items="recentMigrationItems"
            label="Or open a previous migration"
            outlined
            dense
            hide-details
            clearable
            @change="openRecent"
            @click="loadRecentMigrations"
          />
        </v-col>
        <v-col cols="12" md="3">
          <v-btn text small color="primary" @click="loadRecentMigrations"
            >Reload list</v-btn
          >
        </v-col>
      </v-row>

      <div v-if="migration" class="mt-4">
        <v-divider class="mb-3" />
        <div class="summary">
          <span :class="['status-pill', 'status-' + migration.status]">{{
            migration.status
          }}</span>
          <span class="summary-item">Migration #{{ migration.id }}</span>
          <span class="summary-item"
            >Target: <strong>{{ migration.companyName }}</strong> (company
            {{ migration.companyId }}) → default advisor
            {{ migration.targetAdvisorEmail }} (#{{
              migration.targetAdvisorId
            }})</span
          >
        </div>
        <div class="summary mt-1">
          <span class="summary-item">Rows: {{ migration.totalCount }}</span>
          <span class="summary-item">Matched: {{ migration.foundCount }}</span>
          <span v-if="migration.notFoundCount" class="summary-item warn"
            >Not found: {{ migration.notFoundCount }}</span
          >
          <span class="summary-item"
            >Migrated: {{ migration.appliedCount }}</span
          >
          <span v-if="migration.fileName" class="summary-item"
            >File: {{ migration.fileName }}</span
          >
        </div>

        <div class="actions-bar mt-3">
          <v-btn
            rounded
            depressed
            color="secondary"
            :loading="refreshLoading"
            @click="refresh()"
            >Refresh from DB</v-btn
          >
          <v-btn
            rounded
            depressed
            color="primary"
            :loading="executeLoading"
            :disabled="migration.status !== 'previewed'"
            @click="execute()"
            >Execute migration</v-btn
          >
          <v-btn
            rounded
            depressed
            color="error"
            :loading="undoLoading"
            :disabled="migration.status !== 'applied'"
            @click="undo()"
            >Undo migration</v-btn
          >
        </div>

        <div
          class="d-flex align-center mt-3"
          style="gap: 16px; flex-wrap: wrap"
        >
          <v-select
            v-model="roleFilter"
            :items="roleFilterItems"
            label="Filter by role"
            outlined
            dense
            hide-details
            clearable
            style="max-width: 260px"
          />
          <span class="summary-item"
            >Showing {{ filteredActions.length }} of
            {{ migration.actions.length }} rows</span
          >
        </div>

        <v-simple-table dense class="mt-2">
          <template #default>
            <thead>
              <tr>
                <th>Testator ID</th>
                <th>CSV email</th>
                <th>DB email</th>
                <th>Role</th>
                <th>Match</th>
                <th>Current advisor</th>
                <th>Current company</th>
                <th>New advisor</th>
                <th>Undo restores to</th>
                <th>Row status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="action in filteredActions"
                :key="action.id"
                :class="rowClass(action)"
              >
                <td>{{ action.testatorId }}</td>
                <td>{{ action.csvEmail || '—' }}</td>
                <td>{{ action.dbEmail || '—' }}</td>
                <td>{{ action.role || '—' }}</td>
                <td>{{ matchLabel(action) }}</td>
                <td>{{ action.currentAdvisorId || '—' }}</td>
                <td>{{ action.currentCompanyName || '—' }}</td>
                <td>{{ action.newAdvisorId || '—' }}</td>
                <td>{{ action.previousAdvisorId || 'none' }}</td>
                <td>{{ rowStatus(action) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-col>
  </v-row>
</template>

<script>
  import {
    PREVIEW_COMPANY_LEAD_MIGRATION_MUTATION,
    REFRESH_COMPANY_LEAD_MIGRATION_MUTATION,
    EXECUTE_COMPANY_LEAD_MIGRATION_MUTATION,
    UNDO_COMPANY_LEAD_MIGRATION_MUTATION,
    FETCH_COMPANY_LEAD_MIGRATIONS_MUTATION,
  } from '../../constants/graphql/company-lead-migration';

  export default {
    name: 'CompanyLeadMigration',
    data() {
      return {
        companyId: '43',
        file: null,
        migration: null,
        roleFilter: null,
        recentMigrations: [],
        selectedRecentId: null,
        previewLoading: false,
        refreshLoading: false,
        executeLoading: false,
        undoLoading: false,
      };
    },
    computed: {
      recentMigrationItems() {
        return this.recentMigrations.map(migration => ({
          value: migration.id,
          text: `#${migration.id} · ${migration.status} · ${
            migration.companyName || 'company ' + migration.companyId
          } · ${migration.appliedCount}/${migration.totalCount} · ${
            migration.fileName || 'no file'
          }`,
        }));
      },
      migrationId() {
        return this.migration ? this.migration.id : null;
      },
      // Distinct roles present in the current migration, each with a count, for the filter dropdown.
      roleFilterItems() {
        if (!this.migration || !this.migration.actions) return [];
        const counts = {};
        this.migration.actions.forEach(action => {
          const role = action.role || '(no role)';
          counts[role] = (counts[role] || 0) + 1;
        });
        return Object.keys(counts)
          .sort()
          .map(role => ({ value: role, text: `${role} (${counts[role]})` }));
      },
      filteredActions() {
        if (!this.migration || !this.migration.actions) return [];
        if (!this.roleFilter) return this.migration.actions;
        return this.migration.actions.filter(
          action => (action.role || '(no role)') === this.roleFilter
        );
      },
    },
    watch: {
      // Reset the role filter only when a DIFFERENT migration is loaded (preview / open recent),
      // not on refresh/execute/undo of the same one — so the chosen filter survives a refresh.
      migrationId(newId, oldId) {
        if (newId !== oldId) this.roleFilter = null;
      },
    },
    methods: {
      preview() {
        if (!this.file || !this.companyId) return;
        this.previewLoading = true;
        const reader = new FileReader();
        reader.readAsDataURL(this.file);
        reader.onload = evt => {
          this.$apollo
            .mutate({
              mutation: PREVIEW_COMPANY_LEAD_MIGRATION_MUTATION,
              variables: {
                companyId: this.companyId,
                fileBase64: evt.target.result,
                fileName: this.file.name,
              },
            })
            .then(response => {
              this.migration = response.data.previewCompanyLeadMigration;
              this.selectedRecentId = null;
              this.previewLoading = false;
              this.file = null;
            })
            .catch(error => {
              this.previewLoading = false;
              alert(`Error: ${error.message}`);
            });
        };
      },
      refresh() {
        if (!this.migration) return;
        this.refreshLoading = true;
        this.runMigrationMutation(
          REFRESH_COMPANY_LEAD_MIGRATION_MUTATION,
          'refreshCompanyLeadMigration'
        ).finally(() => {
          this.refreshLoading = false;
        });
      },
      execute() {
        if (!this.migration) return;
        const message =
          `Execute migration #${this.migration.id}?\n\n` +
          `${this.migration.foundCount} testator(s) will be reassigned to ` +
          `${this.migration.companyName} (advisor #${this.migration.targetAdvisorId}).\n` +
          `${this.migration.notFoundCount} row(s) could not be matched and will be skipped.\n\n` +
          `You can undo this afterwards.`;
        if (!window.confirm(message)) return;
        this.executeLoading = true;
        this.runMigrationMutation(
          EXECUTE_COMPANY_LEAD_MIGRATION_MUTATION,
          'executeCompanyLeadMigration'
        ).finally(() => {
          this.executeLoading = false;
        });
      },
      undo() {
        if (!this.migration) return;
        const message =
          `Undo migration #${this.migration.id}?\n\n` +
          `${this.migration.appliedCount} testator(s) will be restored to their ` +
          `previous advisor (the "Undo restores to" column).`;
        if (!window.confirm(message)) return;
        this.undoLoading = true;
        this.runMigrationMutation(
          UNDO_COMPANY_LEAD_MIGRATION_MUTATION,
          'undoCompanyLeadMigration'
        ).finally(() => {
          this.undoLoading = false;
        });
      },
      runMigrationMutation(mutation, responseKey) {
        return this.$apollo
          .mutate({
            mutation,
            variables: { migrationId: this.migration.id },
          })
          .then(response => {
            this.migration = response.data[responseKey];
          })
          .catch(error => {
            alert(`Error: ${error.message}`);
          });
      },
      loadRecentMigrations() {
        // Unscoped: list all recent migrations so any prior batch (any company) can be reopened
        // to refresh/execute/undo, regardless of what's currently typed in the company field.
        this.$apollo
          .mutate({
            mutation: FETCH_COMPANY_LEAD_MIGRATIONS_MUTATION,
            variables: { companyId: null },
          })
          .then(response => {
            this.recentMigrations = response.data.fetchCompanyLeadMigrations;
          })
          .catch(error => {
            alert(`Error: ${error.message}`);
          });
      },
      openRecent(migrationId) {
        if (!migrationId) return;
        this.refreshLoading = true;
        this.$apollo
          .mutate({
            mutation: REFRESH_COMPANY_LEAD_MIGRATION_MUTATION,
            variables: { migrationId },
          })
          .then(response => {
            this.migration = response.data.refreshCompanyLeadMigration;
          })
          .catch(error => {
            this.migration = null;
            alert(`Error: ${error.message}`);
          })
          .finally(() => {
            this.refreshLoading = false;
          });
      },
      matchLabel(action) {
        if (!action.found) return '✗ no testator';
        if (!action.csvEmail) return '— no email in file';
        return action.emailMatches ? '✓ email match' : '⚠ email mismatch';
      },
      rowStatus(action) {
        if (action.undone) return 'Undone';
        if (action.applied) return 'Migrated';
        if (!action.found) return 'Not found';
        return 'Pending';
      },
      rowClass(action) {
        if (!action.found) return 'row-error';
        if (action.csvEmail && !action.emailMatches) return 'row-warn';
        return '';
      },
    },
  };
</script>

<style lang="scss" scoped>
  .summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
  }

  .summary-item {
    font-size: 14px;
  }

  .summary-item.warn {
    color: #b71c1c;
    font-weight: bold;
  }

  .status-pill {
    text-transform: uppercase;
    font-size: 12px;
    font-weight: bold;
    padding: 2px 10px;
    border-radius: 12px;
    color: #fff;
    background-color: #607d8b;
  }

  .status-previewed {
    background-color: #1976d2;
  }

  .status-applied {
    background-color: #2e7d32;
  }

  .status-undone {
    background-color: #ef6c00;
  }

  .actions-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .row-error td {
    background-color: #ffebee;
  }

  .row-warn td {
    background-color: #fff8e1;
  }
</style>
